// The card's presentational primitives, lifted out of CategoryCard whole (OLIO-2700 step 1).
// Pure cut-and-paste: no styles changed. Splitting these off is what makes the per-kind card
// registry possible, since every kind's card needs the same row chrome.
import { Link } from 'react-router-dom';
import styled, { css } from 'styled-components';

import Button from 'components/shared/Button';
import { colors } from 'styles/theme/colors';
import { FONT_WEIGHT, FONT_WEIGHT_MEDIUM } from 'styles/theme/textStyles';
import { BodySmall, BodySmallBold } from 'styles/typography';

import { Tone } from '../StatusBadge';

export const Card = styled.div`
  border: 1px solid ${colors.black10};
  border-radius: 4px;
  background: ${colors.white};
  overflow: clip;
`;

// The header tints with the category's state (Figma): flagged reads warm, complete
// green, ready-for-review blue, errored red, processing purple; pending/canceled/skipped
// (neutral) stay white — nothing to draw the eye to — rather than a gray "still loading" look.
// `warning` is the *5 tint (not *10): ActionRow's own flagged substep background is orange5,
// and the header used to run one shade darker than the rows under it — same tone, two
// different swatches. Matching them is what fixes that seam.
export const HEADER_TINTS: Partial<Record<Tone, string>> = {
  warning: colors.orange5,
  success: colors.green10,
  review: colors.blue10,
  danger: colors.red10,
  running: colors.purple5,
  neutral: colors.white,
};

export const HeaderButton = styled.button<{ $open: boolean; $tone: Tone }>`
  width: 100%;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 16px;
  background: ${({ $tone }) => HEADER_TINTS[$tone] ?? colors.black3};
  border: none;
  border-bottom: ${({ $open }) => ($open ? `1px solid ${colors.black10}` : 'none')};
  cursor: pointer;
  text-align: left;
`;

export const HeaderLeft = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;

  > svg {
    flex-shrink: 0;
    margin-top: 4px;
    color: ${colors.black50};
  }
`;

// Single caret that rotates when the section is open — black in both states (the down
// chevron SVG is hardcoded blue, so we rotate the currentColor right chevron instead).
export const Caret = styled.span<{ $open: boolean }>`
  display: inline-flex;
  flex-shrink: 0;
  margin-top: 4px;
  color: ${colors.black50};
  transform: rotate(${({ $open }) => ($open ? '90deg' : '0deg')});
  transition: transform 0.15s ease;
`;

export const CategoryTitle = styled(BodySmallBold)`
  font-size: 15px;
`;

export const Subtitle = styled(BodySmall)`
  margin-top: 4px;
  font-size: 12px;
  color: ${colors.black50};
`;

export const SingleBody = styled.div`
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const StatusOnly = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const Rows = styled.div`
  background: ${colors.white};
`;

// Errored tints red and takes precedence over flagged's orange: a failed run has no result, so
// "needs attention" is the weaker of the two things the row could be saying.
export const ActionRow = styled.div<{ $flagged?: boolean; $errored?: boolean; $clickable?: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px 16px 16px 20px;
  background: ${({ $flagged, $errored }) => {
    if ($errored) return colors.red5;
    return $flagged ? colors.orange5 : 'transparent';
  }};

  & + & {
    border-top: 1px solid ${colors.black5};
  }

  ${({ $clickable, $flagged }) =>
    $clickable &&
    css`
      width: 100%;
      text-align: left;
      cursor: pointer;

      &:hover {
        background: ${$flagged ? colors.orange10 : colors.black5};
      }
    `}
`;

export const IconCell = styled.span`
  flex-shrink: 0;
  margin-top: 2px;
  display: inline-flex;
`;

export const RowMain = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
  flex: 1 1 auto;
`;

// Wraps a control that lives inside a clickable row and does its own thing when pressed.
export const NestedControls = styled.div`
  cursor: default;
`;

// Trailing affordance for a slideout-backed row (hasSlideout) — a chevron pinned to the far right.
export const ChevronCell = styled.span`
  flex-shrink: 0;
  align-self: center;
  display: inline-flex;
  color: ${colors.black50};
`;

// The merged "{Status}: {Name}" row line — a filled pill per row read as too many labels on the
// page, so the status stays a plain colored word (color from StatusIcon's indicatorColor) here.
// Bold only while the row is running or needs attention (isEmphasized) — everything else reads
// at normal weight so the in-flight/blocked rows are what stand out.
export const RowTitle = styled(BodySmallBold)<{ $color?: string; $bold?: boolean }>`
  color: ${({ $color }) => $color ?? colors.black100};
  font-weight: ${({ $bold }) => ($bold ? FONT_WEIGHT_MEDIUM : FONT_WEIGHT)};
`;

export const ActionRequired = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const Choices = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

// OLIO-2686 typography: the summary line was bold; render it regular. 12px to match every other
// substep description/sublabel on the card (Muted, FlaggedCopy, CompletedNote) — these two used
// to run smaller (10px) than the rest. Still the click target that opens the slideout (chevron on
// the right).
export const eligibilityDetailText = css`
  font-family: inherit;
  font-weight: 400;
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0.015em;
`;

export const EligibilityLink = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  text-align: left;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: ${colors.black75};
  ${eligibilityDetailText}

  > svg {
    flex-shrink: 0;
    color: ${colors.black50};
  }
`;

// The last-ran timestamp beneath the summary; turns amber with a warning icon when the check went
// stale (last run before the 1st of the current month).
export const EligibilityTimestamp = styled.div<{ $stale?: boolean }>`
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 6px;
  color: ${({ $stale }) => ($stale ? colors.yellow100 : colors.black50)};
  ${eligibilityDetailText}

  > svg {
    flex-shrink: 0;
    color: ${colors.yellow100};
  }
`;

export const Result = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

export const ResultLabel = styled(BodySmall)`
  color: ${colors.black50};
  font-size: 10px;
`;

export const ResultValue = styled.div`
  font-size: 14px;
  font-weight: normal;
  color: ${colors.black100};
`;

export const ResultLink = styled(Link)`
  margin-left: 8px;
  font-size: 13px;
  font-weight: 400;
  color: ${colors.blue100};
`;

export const SummaryRow = styled.div`
  display: flex;
  gap: 8px;
  font-size: 10px;
`;

export const SummaryName = styled(BodySmall)`
  color: ${colors.black50};
  flex-shrink: 0;
  font-size: 10px;
`;

export const SummaryValue = styled(BodySmall)`
  color: ${colors.black100};
  font-size: 10px;
`;

export const Muted = styled(BodySmall)`
  color: ${colors.black50};
  font-size: 12px;
`;

export const SkippedAside = styled.div`
  margin-left: auto;
  flex-shrink: 0;
  align-self: center;
`;

// A substep row's right-aligned action button (Add Documents, Accept/Deny, View Patient in
// Queue) — smaller than the shared Button's own `small` size, which is sized for standalone use
// (modals, panels) rather than sitting inline in a row.
export const RowButton = styled(Button)`
  font-size: 12px;
  padding: 4px 8px;
`;

export const CompletedNote = styled(BodySmall)`
  display: block;
  font-size: 12px;
  color: ${colors.black75};
`;

export const FlaggedCopy = styled(BodySmall)`
  display: block;
  font-size: 12px;
  color: ${colors.black75};
`;

// The failed-check sentence in a row body. Muted like the other row copy — the row's red tint and
// its "Error:" title already carry the alarm, so repeating it in red here would double it.
export const ErroredNote = styled(BodySmall)`
  color: ${colors.black75};
`;

export const InlineLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: ${colors.blue100};
  font: inherit;
  font-weight: 500;
  text-decoration: underline;

  &:disabled {
    color: ${colors.black50};
    cursor: default;
  }
`;

// Softer than Banner (orange = needs you): this is awareness only, so it reads as a
// note, not an alarm.
export const NoticeBanner = styled(BodySmall)`
  display: block;
  padding: 8px 12px;
  border-radius: 6px;
  border: 1px solid ${colors.black10};
  background: ${colors.black3};
  color: ${colors.black75};
`;

export const BannerTitle = styled(BodySmallBold)`
  color: ${colors.black100};
`;

export const DiffList = styled.ul`
  margin: 0;
  padding-left: 18px;
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-size: 10px;
  color: ${colors.black75};
`;

export const DiffName = styled.span`
  font-weight: 500;
`;

export const DiffOld = styled.span`
  color: ${colors.black50};
  text-decoration: line-through;
`;

export const DiffNew = styled.span`
  font-weight: 500;
  color: ${colors.black100};
`;

export const BannerActions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 4px;
`;

export const StepLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  font: inherit;
  color: ${colors.blue100};
  cursor: pointer;
  text-decoration: underline;
`;
